/**
 * ContainerReservationsPanel — bookings against locations for an inbound
 * container. Renders inside the container detail dialog.
 *
 * Shows warehouse capacity projection at the top (current + projected
 * volume % and pallet count) with an inline warning banner when the
 * projected fill exceeds 100%. The action row exposes "Auto-reserve"
 * (backend picks best-fit empties near receiving) and a manual add form.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Warning, MapPin, MagicWand, Trash, PlusCircle, Stack,
} from '@phosphor-icons/react';
import { Button } from './ui/button';
import {
  getInboundCapacity, listContainerReservations, addContainerReservation,
  removeContainerReservation, autoReserveContainer,
} from '../lib/api';

export default function ContainerReservationsPanel({ container, onChanged }) {
  const [capacity, setCapacity] = useState(null);
  const [reservations, setReservations] = useState(container?.reservations || []);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [autoBusy, setAutoBusy] = useState(false);
  const [form, setForm] = useState({ location_id: '', pallets: 1, volume_m3: 0, line_id: '', note: '' });

  const refresh = async () => {
    if (!container?.id || !container?.warehouse_id) return;
    setLoading(true);
    try {
      const [cap, res] = await Promise.all([
        getInboundCapacity(container.warehouse_id, 30),
        listContainerReservations(container.id),
      ]);
      setCapacity(cap.data);
      setReservations(res.data.reservations || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load capacity');
    } finally { setLoading(false); }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [container?.id, container?.warehouse_id]);

  // Lazy-load available locations for this warehouse to power the dropdown.
  useEffect(() => {
    if (!container?.warehouse_id) return;
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/locations?warehouse_id=${container.warehouse_id}&limit=500`;
    fetch(url, { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => setLocations(Array.isArray(d) ? d : (d.items || [])))
      .catch(() => setLocations([]));
  }, [container?.warehouse_id]);

  const lines = container?.lines || [];
  const warning = capacity?.warning;
  const projected = capacity?.projected;
  const current = capacity?.current;

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.location_id) { toast.error('Pick a location'); return; }
    try {
      await addContainerReservation(container.id, {
        location_id: form.location_id,
        line_id: form.line_id || null,
        pallets: Number(form.pallets || 0),
        volume_m3: Number(form.volume_m3 || 0),
        note: form.note || null,
      });
      setForm({ location_id: '', pallets: 1, volume_m3: 0, line_id: '', note: '' });
      toast.success('Reservation added');
      await refresh();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reservation failed');
    }
  };

  const handleAuto = async () => {
    setAutoBusy(true);
    try {
      const r = await autoReserveContainer(container.id);
      toast.success(`Auto-reserved ${r.data.added} location(s) · ${r.data.reserved_volume_m3.toFixed(2)} m³ covered`);
      await refresh();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Auto-reserve failed');
    } finally { setAutoBusy(false); }
  };

  const handleRemove = async (resId) => {
    try {
      await removeContainerReservation(container.id, resId);
      await refresh();
      onChanged?.();
      toast.success('Reservation released');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Release failed'); }
  };

  const pctColor = (pct) => {
    if (pct == null) return 'text-muted-foreground';
    if (pct >= 100) return 'text-rose-600';
    if (pct >= 85) return 'text-amber-600';
    return 'text-emerald-700';
  };

  return (
    <div className="space-y-3 border-t border-border pt-4" data-testid="container-reservations-panel">
      <div className="flex items-center justify-between gap-2">
        <h4 className="font-bold text-sm flex items-center gap-1"><MapPin size={14} /> Location reservations</h4>
        <Button
          size="sm"
          onClick={handleAuto}
          disabled={autoBusy || !container?.warehouse_id}
          data-testid="auto-reserve-btn"
          className="bg-brand hover:bg-brand/90 h-7 text-xs"
        >
          <MagicWand size={12} className="mr-1" /> {autoBusy ? 'Auto-reserving…' : 'Auto-reserve'}
        </Button>
      </div>

      {/* Capacity projection */}
      {capacity && (
        <div className="border border-border p-3 text-xs" data-testid="reservations-capacity">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Volume · current</div>
              <div className={`text-lg font-bold ${pctColor(current?.volume_pct)}`}>{current?.volume_pct == null ? '—' : `${current.volume_pct}%`}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Volume · projected</div>
              <div className={`text-lg font-bold ${pctColor(projected?.volume_pct)}`}>{projected?.volume_pct == null ? '—' : `${projected.volume_pct}%`}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Pallets · current</div>
              <div className={`text-lg font-bold ${pctColor(current?.pallet_pct)}`}>{current?.used_pallets?.toFixed?.(1) ?? '0'} <span className="text-[10px] font-normal text-muted-foreground">/ {capacity.capacity?.total_pallet_slots ?? 0}</span></div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Pallets · projected</div>
              <div className={`text-lg font-bold ${pctColor(projected?.pallet_pct)}`}>{projected?.pallets?.toFixed?.(1) ?? '0'} <span className="text-[10px] font-normal text-muted-foreground">({projected?.pallet_pct == null ? '—' : `${projected.pallet_pct}%`})</span></div>
            </div>
          </div>
          {warning && (
            <div
              data-testid="reservations-warning"
              className={`mt-3 border ${warning.severity === 'critical' ? 'border-rose-300 bg-rose-50 text-rose-900' : 'border-amber-300 bg-amber-50 text-amber-900'} p-2 text-xs flex items-start gap-2`}
            >
              <Warning size={14} weight="fill" className="mt-0.5" />
              <div>{warning.message}</div>
            </div>
          )}
        </div>
      )}

      {/* Reservation list */}
      <div className="border border-border">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-border bg-muted/50">
              <th className="text-left px-2 py-1.5 text-[10px] uppercase tracking-widest">Location</th>
              <th className="text-left px-2 py-1.5 text-[10px] uppercase tracking-widest">Line (SKU)</th>
              <th className="text-right px-2 py-1.5 text-[10px] uppercase tracking-widest">Pallets</th>
              <th className="text-right px-2 py-1.5 text-[10px] uppercase tracking-widest">m³</th>
              <th className="text-left px-2 py-1.5 text-[10px] uppercase tracking-widest">Note</th>
              <th className="w-8"></th>
            </tr>
          </thead>
          <tbody>
            {reservations.length === 0 ? (
              <tr><td colSpan={6} className="px-2 py-3 text-center text-muted-foreground text-[11px]">No reservations yet. Click <b>Auto-reserve</b> or add one below.</td></tr>
            ) : reservations.map((r) => (
              <tr key={r.id} className="border-b border-border/50" data-testid={`reservation-row-${r.id}`}>
                <td className="px-2 py-1.5 font-mono">{r.location_code || r.location_id.slice(0, 8)}</td>
                <td className="px-2 py-1.5 font-mono text-[10px] text-muted-foreground">{r.line_id ? r.line_id.slice(0, 8) : '— container'}</td>
                <td className="px-2 py-1.5 text-right">{r.pallets?.toFixed?.(1) ?? '—'}</td>
                <td className="px-2 py-1.5 text-right">{r.volume_m3?.toFixed?.(3) ?? '—'}</td>
                <td className="px-2 py-1.5 text-muted-foreground">{r.note || '—'}</td>
                <td className="px-2 py-1.5"><button onClick={() => handleRemove(r.id)} className="text-rose-600 hover:text-rose-800" title="Release" data-testid={`release-reservation-${r.id}`}><Trash size={12} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Manual add row */}
      {!container?.warehouse_id ? (
        <div className="text-[11px] text-muted-foreground p-2 border border-dashed border-border" data-testid="reservations-no-warehouse-hint">
          Assign a warehouse to this container before reserving locations.
        </div>
      ) : (
        <form onSubmit={handleAdd} className="grid grid-cols-1 md:grid-cols-6 gap-2 text-xs items-end">
        <div className="md:col-span-2">
          <label className="block text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Location</label>
          <select value={form.location_id} onChange={(e) => setForm((f) => ({ ...f, location_id: e.target.value }))} className="w-full border border-border rounded-none px-2 py-1 h-8 bg-card" data-testid="reservation-location-select">
            <option value="">Select…</option>
            {locations.map((l) => (
              <option key={l.id} value={l.id}>{l.location_code}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Line (opt.)</label>
          <select value={form.line_id} onChange={(e) => setForm((f) => ({ ...f, line_id: e.target.value }))} className="w-full border border-border rounded-none px-2 py-1 h-8 bg-card" data-testid="reservation-line-select">
            <option value="">— container</option>
            {lines.map((ln, idx) => (
              <option key={ln.id || idx} value={ln.id || idx}>{ln.sku_code || `line ${idx + 1}`}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Pallets</label>
          <input type="number" step="0.1" min="0" value={form.pallets} onChange={(e) => setForm((f) => ({ ...f, pallets: e.target.value }))} className="w-full border border-border rounded-none px-2 py-1 h-8" data-testid="reservation-pallets-input" />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-muted-foreground mb-1">m³</label>
          <input type="number" step="0.001" min="0" value={form.volume_m3} onChange={(e) => setForm((f) => ({ ...f, volume_m3: e.target.value }))} className="w-full border border-border rounded-none px-2 py-1 h-8" data-testid="reservation-volume-input" />
        </div>
        <div>
          <Button type="submit" size="sm" className="w-full h-8" variant="outline" data-testid="add-reservation-btn"><PlusCircle size={12} className="mr-1" /> Add</Button>
        </div>
      </form>
      )}
    </div>
  );
}
